'use client'

import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Heart, Plus, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useUserSettings } from '@/contexts/user-settings-context'

interface FollowButtonProps {
  podcastId: string
  userId?: string
  podcastSnapshot?: {
    id: string
    title: string
    author?: string
    imageUrl?: string
    feedUrl?: string
    defaultLevelRange?: string[] | string | null
  }
  variant?: 'default' | 'outline' | 'ghost'
  size?: 'sm' | 'default' | 'lg'
  className?: string
}

export function FollowButton({ 
  podcastId, 
  userId, 
  podcastSnapshot,
  variant = 'outline',
  size = 'default',
  className = ''
}: FollowButtonProps) {
  const { settings, refreshUserSettings } = useUserSettings()
  const [isLoading, setIsLoading] = useState(false)

  // Get follow status directly from user settings context
  const subscribedPodcastIds = settings?.subscribedPodcastIds || []
  const isFollowing = subscribedPodcastIds.includes(podcastId)

  const handleFollow = async () => {
    if (!userId) {
      toast.error('Please sign in to follow podcasts')
      return
    }

    setIsLoading(true)

    try {
      const response = await fetch(`/api/podcast/${podcastId}/follow`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userId, snapshot: podcastSnapshot })
      })

      if (!response.ok) {
        throw new Error('Failed to update follow status')
      }

      const data = await response.json()
      
      if (data.success) {
        // Refresh user settings to get updated subscription data
        await refreshUserSettings()
        toast.success(data.message)
      } else {
        throw new Error(data.error || 'Failed to update follow status')
      }
    } catch (error) {
      console.error('Error toggling follow:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to update follow status')
    } finally {
      setIsLoading(false)
    }
  }

  // Don't show button if no user
  if (!userId) {
    return null
  }

  return (
    <Button 
      variant={isFollowing ? 'default' : variant}
      size={size}
      onClick={handleFollow}
      disabled={isLoading}
      className={`flex items-center gap-2 ${className}`}
    >
      {isLoading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : isFollowing ? (
        <Heart className="w-4 h-4 fill-current" />
      ) : (
        <Plus className="w-4 h-4" />
      )}
      {isLoading ? 'Updating...' : isFollowing ? 'Following' : 'Follow'}
    </Button>
  )
}
